import React from 'react';
import { Link as RouteLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Flex,
    Icon,
    Heading,
    Tag,
    Stack,
    Text
} from '@chakra-ui/react';

import { GiCalendar, GiTakeMyMoney, GiSpain } from 'react-icons/gi';

import { AnimalClass, AnimalConstants } from '../../../core/animal';

export default function AnimalCard(props: {
    data: AnimalClass;
}): React.ReactElement {
    const { t } = useTranslation();
    const animal = props.data;

    return (
        <Box
            w={'full'}
            bg='white'
            boxShadow='base'
            _hover={{ boxShadow: 'lg' }}
            rounded={'md'}
            p={{ base: 1, md: 2 }}
            overflow={'hidden'}
        >
            <Box
                h={{ base: '180px', md: '200px' }}
                bg={'gray.100'}
                mt={-2}
                mx={-2}
                pos={'relative'}
                bgImage={`url('${animal.getImageFav()}')`}
                bgPosition='center'
                bgRepeat='no-repeat'
                bgSize='cover'
                position={'relative'}
            ></Box>
            <Box m='2'>
                {animal.internalId && (
                    <Text
                        fontSize='xs'
                        fontWeight='bold'
                        color='green.700'
                        noOfLines={1}
                        minH='20px'
                    >
                        {animal.internalId}
                    </Text>
                )}
                <Flex my={animal.internalId ? 0 : 4}>
                    <Icon
                        as={animal.getIcon()}
                        w='24px'
                        h='24px'
                        color='green.700'
                    />
                    <Heading
                        color='green.700'
                        fontSize={'xl'}
                        noOfLines={1}
                        alignItems='2'
                        verticalAlign='middle'
                    >
                        {animal.externalId}
                    </Heading>
                </Flex>

                <Flex justify={'space-around'} my='1' mx='-1'>
                    <Tag
                        variant='solid'
                        colorScheme='teal'
                        mr='1'
                        mb='1'
                        style={{ textTransform: 'uppercase' }}
                        size='sm'
                    >
                        {t(
                            `animal.${animal.specie}.${animal.type}.${
                                animal.gender ||
                                AnimalConstants.animalGenders.male
                            }`
                        )}
                    </Tag>
                    {animal.state && (
                        <Tag
                            variant='solid'
                            colorScheme='blue'
                            mr='1'
                            mb='1'
                            style={{ textTransform: 'uppercase' }}
                            size='sm'
                        >
                            {t(`animal.states.${animal.state}`)}
                        </Tag>
                    )}
                </Flex>

                <Flex justify={'space-around'} align='flex-end' mx='-3'>
                    {animal.land && (
                        <Flex
                            direction={'column'}
                            align='center'
                            maxWidth='45%'
                        >
                            <Icon as={GiSpain} w='20px' h='20px' />
                            <Text
                                fontSize='xs'
                                fontWeight={'bold'}
                                maxWidth='100%'
                                noOfLines={1}
                            >
                                {animal.landData.name}
                            </Text>
                        </Flex>
                    )}

                    <Flex direction={'column'} align='center' maxWidth='15%'>
                        <Icon as={GiTakeMyMoney} w='20px' h='20px' />
                        <Text fontSize='xs' fontWeight={'bold'}>
                            {animal.shell ? 'Si' : 'No'}
                        </Text>
                    </Flex>

                    {animal.birthDate && (
                        <Flex
                            direction={'column'}
                            align='center'
                            maxWidth='40%'
                        >
                            <Icon as={GiCalendar} w='20px' h='20px' />
                            <Text
                                fontSize='xxs'
                                fontWeight={'bold'}
                                maxWidth='100%'
                                noOfLines={1}
                            >
                                {animal.birthDate}
                            </Text>
                        </Flex>
                    )}
                </Flex>
            </Box>
            <Stack mb='2' mt='1' align={'center'}>
                <RouteLink to={t('routes.animals') + `/${animal.id}`}>
                    <Button
                        colorScheme='green'
                        display={'block'}
                        m='auto'
                        size='sm'
                    >
                        {t('global.seeDetails')}
                    </Button>
                </RouteLink>
            </Stack>
        </Box>
    );
}
